import React, { useState } from "react";
import "./CalculatorApp.css"; // We'll create this CSS file

function CalculatorApp() {
  // Input states
  const [dirham, setDirham] = useState("");
  const [xau, setXau] = useState("");
  const [dollarStart, setDollarStart] = useState("");
  const [iranInfl, setIranInfl] = useState("");
  const [usInfl, setUsInfl] = useState("");
  const [liq, setLiq] = useState("");
  const [gdp, setGdp] = useState("");

  // Parse input as floats (use 0 if empty)
  const D = parseFloat(dirham) || 0;
  const AU = parseFloat(xau) || 0;
  const DP = parseFloat(dollarStart) || 0;
  const IIR = parseFloat(iranInfl) || 0;
  const IUS = parseFloat(usInfl) || 0;
  const LQ = parseFloat(liq) || 0;
  const GDP = parseFloat(gdp) || 0;

  // All your existing calculations remain the same
  const dollarPrice = D * 3.675;
  const A = (IIR - IUS) / 100;
  const B = (LQ - GDP) / 100;
  const dollarInflation = DP * (A + 1);
  const dollarGDP = DP * (B + 1);
  const mithqal18 = (AU * dollarPrice) / 8.999;
  const wholeCoin = (AU * dollarPrice) / 4.24927;
  const gold24 = (AU * dollarPrice) / 31.103431;
  const gram18 = mithqal18 / 4.608;
  const V = gram18 > 0 ? (gold24 / gram18) * 31.103431 : 0;
  const dollarVal18 = gram18 > 0 ? (gram18 * V) / AU : 0;
  const gold18Val = gram18 > 0 && gold24 > 0 ? (AU * dollarVal18) / 31.103431 * (gram18 / gold24) : 0;
  const iranMithqal = (AU * dollarPrice) / 9.5742;
  const targetDollar = wholeCoin > 0 ? (wholeCoin * 31.103431 ) / (AU * 8.133 * 0.9) : 0;
  const gramFromIranMithqal = iranMithqal / 4.3318;
  const mithqalFromGram = gram18 * 4.3318;

  const formatNumber = (num) => {
    return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  return (
    <div className="calculator-container">
      <div className="calculator-header">
        <h1>Financial Calculator</h1>
        <p>Calculate dollar prices, gold values, and inflation metrics</p>
      </div>

      <div className="calculator-body">
        <div className="inputs-section">
          <h2>Input Parameters</h2>
          
          <div className="input-grid">
            <div className="input-group">
              <label htmlFor="dirham">UAE Dirham Rate</label>
              <input
                id="dirham"
                type="number"
                value={dirham}
                onChange={(e) => setDirham(e.target.value)}
                placeholder="Enter dirham rate"
                className="input-field"
              />
            </div>

            <div className="input-group">
              <label htmlFor="xau">Gold Price (XAU)</label>
              <input
                id="xau"
                type="number"
                value={xau}
                onChange={(e) => setXau(e.target.value)}
                placeholder="Enter gold price"
                className="input-field"
              />
            </div>

            <div className="input-group">
              <label htmlFor="dollarStart">Starting Dollar Price</label>
              <input
                id="dollarStart"
                type="number"
                value={dollarStart}
                onChange={(e) => setDollarStart(e.target.value)}
                placeholder="Enter starting price"
                className="input-field"
              />
            </div>

            <div className="input-group">
              <label htmlFor="iranInfl">Iran Inflation Rate (%)</label>
              <input
                id="iranInfl"
                type="number"
                value={iranInfl}
                onChange={(e) => setIranInfl(e.target.value)}
                placeholder="Enter Iran inflation"
                className="input-field"
              />
            </div>

            <div className="input-group">
              <label htmlFor="usInfl">US Inflation Rate (%)</label>
              <input
                id="usInfl"
                type="number"
                value={usInfl}
                onChange={(e) => setUsInfl(e.target.value)}
                placeholder="Enter US inflation"
                className="input-field"
              />
            </div>

            <div className="input-group">
              <label htmlFor="liq">Liquidity Rate (%)</label>
              <input
                id="liq"
                type="number"
                value={liq}
                onChange={(e) => setLiq(e.target.value)}
                placeholder="Enter liquidity rate"
                className="input-field"
              />
            </div>

            <div className="input-group">
              <label htmlFor="gdp">GDP Rate (%)</label>
              <input
                id="gdp"
                type="number"
                value={gdp}
                onChange={(e) => setGdp(e.target.value)}
                placeholder="Enter GDP rate"
                className="input-field"
              />
            </div>
          </div>
        </div>

        <div className="results-section">
          <h2>Calculated Results</h2>
          
          <div className="results-grid">
            <div className="result-card dollar-prices">
              <h3>💵 Dollar Calculations</h3>
              <div className="result-item">
                <span className="label">Dollar Price (from Dirham):</span>
                <span className="value">{formatNumber(dollarPrice)}</span>
              </div>
              <div className="result-item">
                <span className="label">Dollar Price (Inflation):</span>
                <span className="value">{formatNumber(dollarInflation)}</span>
              </div>
              <div className="result-item">
                <span className="label">Dollar Price (GDP):</span>
                <span className="value">{formatNumber(dollarGDP)}</span>
              </div>
              <div className="result-item highlight">
                <span className="label">Target Dollar Price:</span>
                <span className="value">{formatNumber(targetDollar)}</span>
              </div>
            </div>

            <div className="result-card gold-prices">
              <h3>🥇 Gold Calculations</h3>
              <div className="result-item">
                <span className="label">18K Mithqal:</span>
                <span className="value">{formatNumber(mithqal18)}</span>
              </div>
              <div className="result-item">
                <span className="label">Whole Coin:</span>
                <span className="value">{formatNumber(wholeCoin)}</span>
              </div>
              <div className="result-item">
                <span className="label">24K Gold (per gram):</span>
                <span className="value">{formatNumber(gold24)}</span>
              </div>
              <div className="result-item">
                <span className="label">18K Gold (per gram):</span>
                <span className="value">{formatNumber(gram18)}</span>
              </div>
              <div className="result-item">
                <span className="label">18K Gold Value:</span>
                <span className="value">{formatNumber(gold18Val)}</span>
              </div>
            </div>

            <div className="result-card iranian-market">
              <h3>🇮🇷 Iranian Market</h3>
              <div className="result-item">
                <span className="label">Iranian Mithqal:</span>
                <span className="value">{formatNumber(iranMithqal)}</span>
              </div>
              <div className="result-item">
                <span className="label">Gram from Iranian Mithqal:</span>
                <span className="value">{formatNumber(gramFromIranMithqal)}</span>
              </div>
              <div className="result-item">
                <span className="label">Mithqal from Gram:</span>
                <span className="value">{formatNumber(mithqalFromGram)}</span>
              </div>
            </div>

            <div className="result-card inflation-metrics">
              <h3>📊 Economic Metrics</h3>
              <div className="result-item">
                <span className="label">Inflation Spread (A):</span>
                <span className="value">{(A * 100).toFixed(2)}%</span>
              </div>
              <div className="result-item">
                <span className="label">Liquidity-GDP Spread (B):</span>
                <span className="value">{(B * 100).toFixed(2)}%</span>
              </div>
              <div className="result-item">
                <span className="label">Variable V:</span>
                <span className="value">{formatNumber(V)}</span>
              </div>
              <div className="result-item">
                <span className="label">18K Dollar Value:</span>
                <span className="value">{formatNumber(dollarVal18)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CalculatorApp;
